import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  CheckCircle,
  XCircle,
  Clock,
} from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [status, setStatus] = useState("pending");

  const subscriptionId = params.get("subscription_id");

  useEffect(() => {
    if (!subscriptionId) {
      nav("/pricing");
      return;
    }

    let attempts = 0;
    let timer;

    const poll = async () => {
      attempts += 1;

      try {
        const { data } = await api.get(
          `/payments/status/${encodeURIComponent(subscriptionId)}`
        );

        if (data.payment_status === "trial") {
          setStatus("trial");
          await refresh();
          return;
        }

        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }

        if (
          data.status === "cancelled" ||
          data.status === "halted" ||
          data.status === "expired"
        ) {
          setStatus("expired");
          return;
        }

        if (attempts < 8) {
          timer = setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch (err) {
        console.error("Payment status check failed", err);
        setStatus("error");
      }
    };

    poll();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [subscriptionId, nav, refresh]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#0F0F12] border border-white/10 rounded-2xl p-8 text-center">

        {status === "paid" && (
          <>
            <CheckCircle
              size={64}
              weight="fill"
              className="text-green-400 mx-auto"
            />

            <h1 className="text-2xl font-medium mt-6">
              Payment successful
            </h1>

            <p className="text-zinc-400 mt-3">
              Your subscription is now active.
            </p>

            <button
              onClick={() => nav("/dashboard")}
              className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === "trial" && (
          <>
            <CheckCircle
              size={64}
              weight="fill"
              className="text-green-400 mx-auto"
            />

            <h1 className="text-2xl font-medium mt-6">
              Your 7-day Pro trial is active
            </h1>

            <p className="text-zinc-400 mt-3">
              Your recurring Pro subscription is authorized. You will be charged the normal Pro monthly price after the 7-day trial unless you cancel first.
            </p>

            <button
              onClick={() => nav("/dashboard")}
              className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === "pending" && (
          <>
            <Clock
              size={64}
              weight="fill"
              className="text-yellow-400 mx-auto"
            />

            <h1 className="text-2xl font-medium mt-6">
              Confirming your payment
            </h1>

            <p className="text-zinc-400 mt-3">
              Please wait while we confirm your subscription.
            </p>
          </>
        )}

        {status === "expired" && (
          <>
            <XCircle
              size={64}
              weight="fill"
              className="text-red-400 mx-auto"
            />

            <h1 className="text-2xl font-medium mt-6">
              Payment not completed
            </h1>

            <p className="text-zinc-400 mt-3">
              Your subscription could not be activated.
            </p>

            <button
              onClick={() => nav("/pricing")}
              className="mt-8 w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium py-3 rounded-lg transition-colors"
            >
              Back to Pricing
            </button>
          </>
        )}

        {status === "timeout" && (
          <>
            <Clock
              size={64}
              weight="fill"
              className="text-yellow-400 mx-auto"
            />

            <h1 className="text-2xl font-medium mt-6">
              Payment is being processed
            </h1>

            <p className="text-zinc-400 mt-3">
              Your payment may still be processing. Please check your account
              shortly.
            </p>

            <button
              onClick={() => nav("/dashboard")}
              className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle
              size={64}
              weight="fill"
              className="text-red-400 mx-auto"
            />

            <h1 className="text-2xl font-medium mt-6">
              Unable to confirm payment
            </h1>

            <p className="text-zinc-400 mt-3">
              Please check your account or try again from the pricing page.
            </p>

            <button
              onClick={() => nav("/pricing")}
              className="mt-8 w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium py-3 rounded-lg transition-colors"
            >
              Back to Pricing
            </button>
          </>
        )}

      </div>
    </div>
  );
}
